/**
 * The boxes the list strip is built from (score-controls.tsx), as class strings. A LEAF module for the same
 * reason as `pane-metrics`: the pending UIs have to paint the strip's exact geometry and live in the entry
 * bundle, so importing it from `score-controls.tsx` would drag the router, lingui and the indicator palette
 * in with them. Sharing the strings is also what stops a skeleton drifting a pixel off the real strip.
 */

/** `specific` ranks by the ámbito the report is measured on; `global` ranks by the total. */
export type RankCriterion = 'specific' | 'global'

/**
 * How the strip's bottom rule ends. `panel`: the list sits between two panes, so the rule is solid across it.
 * `left`: the list runs to the window's own edge, where a rule that reaches it reads as the lid of a box, so
 * it fades out on the same ramp as every other rule in the stack (app.css `.rule-fade-left`). Both keep the
 * inset shadow, which redraws the PANE DIVIDER across the pixels the strip bleeds into — `rule-fade-y`'s
 * ::after on CollapsiblePanelContent, which this strip's full-width background would otherwise paint over.
 */
export type StripEdge = 'panel' | 'left'

// `md:sticky`, not plain `sticky`: above `md` ScrollableList IS the scroller, so this pins to the top of it;
// below it the DOCUMENT scrolls and a sticky head would float free of the list it heads. The negative margins
// let it span the list's own gutters so its rule reaches both edges.
export const STRIP_CHROME =
  'bg-background z-10 -mx-3 mb-1 pr-3 pl-5 shadow-[inset_-1px_0_0_0_var(--border)] md:sticky md:top-0'

// ONE vertical rhythm, written twice because the two rules occupy different space. A real `border-b` renders
// OUTSIDE the padding box; a faded rule is a pseudo-element painted INSIDE it, and app.css is unlayered, so
// its `bottom: 0` cannot be overridden from a utility. The faded variant therefore buys that pixel back off
// the SAME spacing step, so the two stay in step when the step changes.
export const STRIP_PAD_Y = 'py-2'
const STRIP_PAD_Y_FADED = 'pb-[calc(var(--spacing)*2+1px)]'

export const STRIP_EDGE: Record<StripEdge, string> = {
  panel: 'border-b',
  left: `rule-fade-left relative ${STRIP_PAD_Y_FADED}`,
}

/** The strip's own row: the count on the left, the controls packed to the right. */
export const STRIP_ROW = 'flex items-center gap-2.25 px-2'

/** The height every head, group and standalone control on the strip shares. */
export const STRIP_GROUP_HEIGHT = 'h-7'

/** The box one choice's heads share (ControlGroup in score-controls.tsx). */
export const CONTROL_GROUP_BOX = 'flex h-7 shrink-0 items-center gap-0.5 rounded-md border p-px'

// 9px is what fits the four-character abbreviations in the width a side panel can spare. The border is
// transparent when resting so lighting a chip never shifts the row by two pixels.
export const CONTROL_HEAD_TYPE =
  'flex items-center gap-px border border-transparent text-[9px] leading-none font-bold uppercase'

/** A head inside a group: it sits in the box's 2px inset, which is what the smaller radius keeps concentric. */
export const GROUPED_HEAD_BOX = 'h-6 rounded-sm px-1.5'
